// HomepageBlock.tsx
import React, { useEffect, useMemo } from 'react';
import { compose } from 'recompose';
import styled, { cx, StylesProps } from '../../../../../lib/styles';
import homepageStyles from './HomepageBlock_styles';
import { useAlgoliaSearch } from '../../../../../lib/hooks/useAlgoliaSearch';
import { algoliaDataToRetrieve, ArticleItem } from '../../../../../lib/algolia/types';
import ArticleTileBlock, { ArticleTileColumn } from '../../../../../components/presentational/ArticleTileBlock/ArticleTileBlock';
import { useAlgoliaObjects } from '../../../../../lib/hooks/useAlgoliaObjects';
import { GetObjectsOptions, SearchOptions } from '@algolia/client-search'; // Import Algolia types
import SkeletonLoader from '../../../../../components/presentational/SkeletonLoader/SkeletonLoader';
import { cfImage } from '../../../../../lib/stringUtils';
import Spacer from '../../../../../components/presentational/Spacer';
import theme from "../../../../../lib/theme";
import _ from 'lodash';
const { mediaSizes } = theme;

export interface NavSection {
  id: number;
  label: string;
  excluded_categories?: string[];
  excluded_tags?: string[];
  excluded_object_ids?: string[];
  children?: NavSection[];
}

export interface HomepageBlockData {
  type: 'homepage_block';
  hero_articles: { id: number }[];
  nav_sections: NavSection[];
  nav_sections_sidebar_posts: { id: number }[];
}

interface HomepageBlockProps {
  block: HomepageBlockData;
  styles: StylesProps<typeof homepageStyles>;
}

const ALIAS_FOR_YOUR_INDEX = 'production_media_starlord'; // Define your Algolia index name

/**
 * HomepageBlock component
 * @param block - The block data passed from Wordpress
 * @param styles - Styles for the component
 * @returns JSX.Element
 */
const HomepageBlock = ({
  block,
  styles,
}: HomepageBlockProps & StylesProps<typeof homepageStyles>) => {
  const TOTAL_ARTICLES_PER_BLOCK = 5; // Total articles to fetch for hero and nav sections
  const [trendingArticlesUsed, setTrendingArticlesUsed] = React.useState(0);

  const trendingSearchOptions = useMemo<SearchOptions>(() => ({
    // similarQuery: combinedSearchQuery,
    filters: `categories.name:"global-latest" OR tags:"global-latest"`,
    hitsPerPage: 10, // You can specify the number of results here
    attributesToRetrieve: algoliaDataToRetrieve,
  }), []); // Re-memoize if filters change

  const {
    data: trendingArticles,
    loading: trendingLoading,
    error: trendingError,
  } = useAlgoliaSearch<ArticleItem>(ALIAS_FOR_YOUR_INDEX, '', trendingSearchOptions); // Empty query string as we are using filters

  // --- 1. Query Hero Articles by Object IDs ---
  const heroArticleIds = useMemo(() => {
    return block.hero_articles.map(article => `post#${article.id.toString()}`);
  }, [block.hero_articles]);

  const heroArticlesAlgoliaOptions = useMemo<GetObjectsOptions>(() => ({
    attributesToRetrieve: algoliaDataToRetrieve,
  }), []);

  const {
    data: heroArticles,
    loading: heroArticlesLoading,
    error: heroArticlesError,
  } = useAlgoliaObjects<ArticleItem>(heroArticleIds, ALIAS_FOR_YOUR_INDEX, heroArticlesAlgoliaOptions);

  useEffect(() => {
    // Backfill hero articles with trending articles if not enough hero articles are fetched
    if (
      !heroArticlesLoading && heroArticles && heroArticles.length < TOTAL_ARTICLES_PER_BLOCK && // hero articles needs backfilling
      trendingArticlesUsed < (trendingArticles || []).length && // are there any trending articles left to use?
      !trendingLoading && trendingArticles && trendingArticles.length > 0 // trending articles are loaded
    ) {
      const totalHeroBackfill = TOTAL_ARTICLES_PER_BLOCK - heroArticles.length;
      // Backfill with trending articles
      heroArticles.push(...(trendingArticles.slice(trendingArticlesUsed, (trendingArticlesUsed + totalHeroBackfill)) || []));
      setTrendingArticlesUsed(trendingArticlesUsed + totalHeroBackfill);
    }
  }, [heroArticles, heroArticlesLoading, trendingArticles, trendingLoading]);

  // This will store results for each nav section, including its children
  const fetchedNavSections: { 
    section: NavSection; 
    articles: ArticleItem[] | null; 
    loading: boolean; 
    error: Error | null, 
    searchOptions: SearchOptions 
  }[] = [];

  block.nav_sections.filter(n => n.children && n.children.length > 0).forEach(navSection => {
    // Collect all relevant names for the current section and its children
    const queryNames: string[] = [navSection.label];
    navSection.children?.forEach(child => {
			if (!child.label || typeof child.label !== 'string' || child.label === 'undefined	') return;
			queryNames.push(child.label);
		});
    // Construct the filters based on excluded_categories, excluded_tags, and excluded_object_ids
    const filters: string[] = [];

    // Add filter to exclude items with empty or null image property
    // 'image:!""' means 'image' attribute exists and is not an empty string.
    // If you also need to exclude items where 'image' attribute itself doesn't exist, you might add 'image:*' to ensure it's present,
    // but typically 'image:!""' is sufficient for string fields.
    // filters.push('image:!""');

    // Category and Tag Filters (OR'ed for inclusion)
    const categoryOrTagFilters: string[] = [];
    queryNames.forEach(name => {
      categoryOrTagFilters.push(`categories.name:"${name}"`);
      categoryOrTagFilters.push(`tags:"${name}"`);
    });
    if (categoryOrTagFilters && categoryOrTagFilters.length > 0) {
      filters.push(`(${categoryOrTagFilters.join(' OR ')})`);
    }

    // const combinedSearchQuery = queryNames
    //   .slice(0, 10) // Limit to first ten names for the search query
    //   .map(name => name.split(' ')) // Example: ["Real Estate", "House"] -> [["Real", "Estate"], ["House"]]
    //   .reduce((acc, val) => acc.concat(val), []) // Flatten: ["Real", "Estate", "House"]
    //   .filter(word => word.trim() !== '') // Clean up: Remove "" from splitting " "
    //   .map(word => word.toLowerCase()) // Lowercase: ["real", "estate", "house"]
    //   .join(' OR '); // Join: "real OR estate OR house"

    // Exclude Categories
    if (navSection.excluded_categories && navSection.excluded_categories.length > 0) {
      const excludedCategoryFilters = navSection.excluded_categories.map(cat => `NOT categories.name:"${cat}"`);
      filters.push(`(${excludedCategoryFilters.join(' AND ')})`);
    }

    // Exclude Tags
    if (navSection.excluded_tags && navSection.excluded_tags.length > 0) {
      const excludedTagFilters = navSection.excluded_tags.map(tag => `NOT tags:"${tag}"`);
      filters.push(`(${excludedTagFilters.join(' AND ')})`);
    }

    // Exclude Object IDs - exclude trending articles that are already used for backfill
    const combinedExcludedObjects = (navSection.excluded_object_ids || []).concat(trendingArticles?.slice(0, trendingArticlesUsed).map(article => `${article.objectID}`) || []);
    if (combinedExcludedObjects && combinedExcludedObjects.length > 0) {
      const excludedObjectFilters = combinedExcludedObjects.map(id => `NOT objectID:"${id}"`);
      filters.push(`(${excludedObjectFilters.join(' AND ')})`);
    }

    // console.log(`Combined Excluded Objects for ${navSection.label}:`, { 
    //   combinedExcludedObjects, 
    //   trendingArticles,
    //   sectionArticles: navSection?.children?.map(c => c.id) || [],
    //   remainingTrendingArticles: trendingArticles?.slice(trendingArticlesUsed) || [],
    //   trendingArticlesUsed,
    //   filters,
    // });

    const navSectionSearchOptions = useMemo<SearchOptions>(() => ({
      // similarQuery: combinedSearchQuery,
      filters: filters.join(' AND '),
      hitsPerPage: 10, // You can specify the number of results here
      attributesToRetrieve: algoliaDataToRetrieve,
    }), [filters.join(' OR ')]); // Re-memoize if filters change

    const {
      data: navSectionArticles,
      loading: navSectionLoading,
      error: navSectionError,
    } = useAlgoliaSearch<ArticleItem>(ALIAS_FOR_YOUR_INDEX, '', navSectionSearchOptions); // Empty query string as we are using filters

    // console.log('Nav Sections Filters:', { navSection, navSectionArticles, navSectionSearchOptions });

    fetchedNavSections.push({
      section: navSection,
      articles: navSectionArticles,
      loading: navSectionLoading,
      error: navSectionError,
      searchOptions: navSectionSearchOptions,
    });
  });

  //#region Future Feature: Sidebar Posts
  // const sidebarSearchOptions = useMemo<SearchOptions>(() => ({
  //   // similarQuery: combinedSearchQuery,
  //   filters: `categories.name:"global-latest" OR tags:"global-latest"`,
  //   hitsPerPage: 10, // You can specify the number of results here
  //   attributesToRetrieve: algoliaDataToRetrieve,
  // }), []); // Re-memoize if filters change

  // const {
  //   data: sidebarArticles,
  //   loading: sidebarLoading,
  //   error: sidebarError,
  // } = useAlgoliaSearch<ArticleItem>(ALIAS_FOR_YOUR_INDEX, '', sidebarSearchOptions); // Empty query string as we are using filters
  //#endregion Future Feature: Sidebar Posts

  /*  DEBUGGING */
  // useEffect(() => {
  //   // Log initial block data and loading states for debugging
  //   if (trendingLoading || heroArticlesLoading || fetchedNavSections.some(section => section.loading)) return;

  //   console.log('HomepageBlock mounted with block:', { 
  //     block,
  //     heroArticles, heroArticlesLoading, heroArticlesError,
  //     trendingArticles, trendingLoading, trendingError,
  //     fetchedNavSections
  //   });
  // }, [
  //   heroArticles,
  //   heroArticlesLoading,
  //   fetchedNavSections,
  // ]);

  return (
    <Spacer space={0} align={'center'}>
      <div
        dangerouslySetInnerHTML={{ __html: `
          <script>
            window.googletag = window.googletag || {cmd: []};
            googletag.cmd.push(function() {
              googletag.defineSlot('/21875548389/collection_sas_sticky', [[300, 600],[300, 250]], 'gpt-ad-21875548389-collection_sas_sticky-0').addService(googletag.pubads());
              googletag.pubads().enableSingleRequest();
              googletag.enableServices();
            });
          </script>        
        `}}
      ></div>
      <Spacer space={'s'} width={mediaSizes.xxl ?? '1200px'} justify={'center'} className={cx(`homepage-layout`, styles.homepageLayout)}>
        {(heroArticles && heroArticles.length > 0 && heroArticlesLoading === false) ? (
          <>
            <ArticleTileBlock
              // title="Hero Articles"
              cssClasses="hero-articles"
              blockConfig={{ showTitleLinks: false }} // Show title links for this block
              columns={
                [
                  {
                    width: '50%',
                    columnConfig: {
                      showColumnNav: false,
                    },
                    tileDirection: 'row',
                    articles: heroArticles
                      .filter(a => a.image && a.image !== '')
                      .slice(0,1)
                      .map(article => {
                        return {
                          article: {
                            ...article,
                            image: cfImage(article.image, { width: 800 }),
                          },
                          options: {
                            textPosition: 'overlay',
                            isCoverImage: true, // Assuming these are cover images  
                            tileWidth: '100%',
                          },
                          cssClasses: 'hero-article-col-1',
                        };
                      }) || [],
                      cssClasses: `flex-grow-1`,
                  },
                  {
                    width: '50%',
                    columnConfig: {
                      showColumnNav: false,
                    },
                    tileDirection: 'row',
                    articles: heroArticles
                      .filter(a => a.image && a.image !== '')
                      .slice(1,5)
                      .map(article => {
                        const titleWords = article.title.split(/[ -]+/).filter(Boolean);
                        const title = titleWords.length > 9 ?
                          `${titleWords.slice(0, 8).join(' ')}...`.trim() :
                          article.title;
                        return {
                          article: {
                            ...article,
                            image: cfImage(article.image, { width: 400 }),
                            title,
                          },
                          options: {
                            textPosition: 'overlay',
                            isCoverImage: true, // Assuming these are cover images  
                            tileWidth: '50%',
                          },
                          cssClasses: 'hero-article-col-2',
                        };
                      }) || [],
                      cssClasses: `show-no-meta`
                  },
                ] as ArticleTileColumn[]
              }
            />
          </>
        ) : <SkeletonLoader loaderType={'FeatureTileBlock'} />}

        <div className={cx(`layout-columns d-flex`, styles.layoutColumns)} style={{ gap: '1em' }}>
          <div
            style={{ flex: `1 0 calc(900px - 1em)`, display: 'flex', flexDirection: 'column', gap: '0.5em' }}  
          >
          {(fetchedNavSections && fetchedNavSections.length > 0) ? 
            (
              <>
                {fetchedNavSections.map((sectionData, index) => {
                  // console.log('Rendering section:', { sectionData });
                  if (sectionData.articles && sectionData.articles.length > 0) {
                    return (
                      <div key={sectionData.section.id || `nav-section-${index}`} className={`${sectionData.section.label.toLowerCase().replace(/\s+/g, '-')}-section`}>
                        <ArticleTileBlock
                          key={sectionData.section.id || `nav-section-${index}`} // Use a unique key
                          title={_.omit(sectionData.section, 'children')}
                          subCategories={sectionData.section.children || []}
                          blockConfig={{ showTitleLinks: true }} // Show title links for this block
                          columns={
                            [
                              {
                                width: '60%',
                                tileDirection: 'row',
                                articles: sectionData.articles
                                  .filter(a => a.image && a.image !== '')
                                  .slice(0,1)
                                  .map(article => {
                                    return {
                                      article: {
                                        ...article,
                                        image: cfImage(article.image, { width: 800 }),
                                      },
                                      options: {
                                        textPosition: 'bottom',
                                        isCoverImage: true, // Assuming these are cover images  
                                        tileWidth: '100%',
                                      },
                                      cssClasses: 'nav-section-col-1',
                                    };
                                  }) || [],
                                  cssClasses: `flex-grow-1`,
                              },
                              {
                                width: '40%',
                                tileDirection: 'column',
                                articles: sectionData.articles
                                  .filter(a => a.image && a.image !== '')
                                  .slice(1,5)
                                  .map(article => {
                                    const titleWords = article.title.split(/[ -]+/).filter(Boolean);
                                    return {
                                      article: {
                                        ...article,
                                        image: cfImage(article.image, { width: 400 }),
                                        title: titleWords.length > 10 ?
                                          `${titleWords.slice(0, 9).join(' ')}...`.trim() :
                                          article.title,
                                      },
                                      options: {
                                        textPosition: 'right',
                                        tileWidth: '100%',
                                      },
                                      cssClasses: 'nav-section-col-2',
                                    };
                                  }) || [],
                                  columnConfig: {
                                    showColumnNav: true,
                                  },
                                  cssClasses: `show-no-meta`,
                              },
                            ] as ArticleTileColumn[]
                          }
                        />
                      </div>
                    );
                  }
                  return null; // Skip rendering if no articles
                })}
              </>
            ) : <SkeletonLoader loaderType={'TileBlock'} />}
          </div>
          <div
            style={{ 
              flex: `0 0 300px`, 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.5em',
              position: 'relative',
              justifyContent: 'flex-start',
            }}
          >
            {/* {(sidebarArticles && sidebarArticles.length > 0 && sidebarLoading === false) ? (
              <ArticleTileBlock
                title={{
                  label: "You may also like",
                  cssClasses: 'alt-color',
                }}
                cssClasses="sidebar-posts"
                columns={
                  [
                    {
                      width: '100%',
                      tileDirection: 'row',
                      articles: sidebarArticles
                        .filter(a => a.image && a.image !== '')
                        .slice(0, 4)
                        .map(article => {
                          const titleWords = article.title.split(/[ -]+/).filter(Boolean);
                          const title = titleWords.length > 9 ?
                            `${titleWords.slice(0, 8).join(' ')}...`.trim() :
                            article.title;
                          return {
                            article: {
                              ...article,
                              image: cfImage(article.image, { width: 400 }),
                              title,
                            },
                            textPosition: 'bottom',
                            tileWidth: '50%',
                            cssClasses: 'sidebar-col-1',
                          };
                        }) || [],
                      cssClasses: `show-no-meta`,
                    },
                  ] as ArticleTileColumn[]
                }
              />
            ) : <SkeletonLoader loaderType={'TileBlock'} />} */}

            <div className={cx(`sidebar-ad-container d-flex flex-column`)}
              style={{
                position: 'sticky', 
                top: '10px',
                gap: '0.5em',
              }}>
              {(trendingArticles && trendingArticles.length > 0 && trendingLoading === false) ? (
                <ArticleTileBlock
                  title={{
                    label: "What's New",
                    cssClasses: 'alt-color alt-size-small',
                  }}
                  blockConfig={{
                    showTitleLinks: true, // Show title links for this block
                  }}
                  cssClasses="trending-posts"
                  columns={
                    [
                      {
                        width: '100%',
                        tileDirection: 'row',
                        articles: trendingArticles
                          .slice(trendingArticlesUsed, trendingArticlesUsed + 4)
                          .filter(a => a.image && a.image !== '')
                          .slice(0, 4)
                          .map(article => {
                            const titleWords = article.title.split(/[ -]+/).filter(Boolean);
                            const title = titleWords.length > 9 ?
                              `${titleWords.slice(0, 8).join(' ')}...`.trim() :
                              article.title;
                            return {
                              article: {
                                ...article,
                                image: cfImage(article.image, { width: 400 }),
                                title,
                              },
                              options: {
                                textPosition: 'bottom',
                                tileWidth: '50%',
                              },
                              cssClasses: 'trending-col-1',
                            };
                          }) || [],
                        cssClasses: `show-no-meta`,
                      },
                    ] as ArticleTileColumn[]
                  }
                />
              ) : <SkeletonLoader loaderType={'TileBlock'} />}
              <div className={cx(`ad`, styles.ad)}>
                <div id="gpt-ad-21875548389-collection_sas_sticky-0" 
                style={{ minWidth: '300px', minHeight: '250px' }}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: `
                      <script>
                        googletag.cmd.push(function() { googletag.display('gpt-ad-21875548389-collection_sas_sticky-0'); });
                      </script>
                    ` }}
                  ></div>
                </div>									
              </div>
            </div>
          </div>
        </div>
      </Spacer>
    </Spacer>
  );
};

export default compose(styled(homepageStyles))(HomepageBlock);